import {
    ON_ROW_INPUT_CHANGE,
    INSERT_PARTIAL,
    INSERT_ROW,
    REMOVE_ROW,
    ON_REMOVE_FOCUS,
    ON_META_FIELD_CHANGE,
    CLEAR_ALL_FIELDS
} from '../actions/AppActions';

import {
    insertItem,
    removeItem,
    updateFieldObjectInArray,
    doesRowHavePartials,
    rowContainsError,
    rowIsIncomplete
} from '../Utils';

import {
    RowFields,
    MetaFields,
    PARTIAL_FIELDS
} from '../Constants';

const createEmptyFieldsObj = (rowType, billNumber) => {
    const newRow = Object.values(RowFields).reduce((agg, fieldObj) =>
        Object.assign(agg, { [fieldObj.id]: { value: '', errMsg: '', displayError: false } })
        , { rowType })
    const x = setBillNumberInRowData(newRow, billNumber)
    return x;
}

const createRow = (rowId, billNumber, rowType) => {
    const emptyRow = createEmptyFieldsObj(rowType);
    const x = setBillNumberInRowData(emptyRow, billNumber);
    const y = setRowIdInRowData(x, rowId);
    return y;
}

const setRowIdInRowData = (rowData, rowId) => ({
    ...rowData,
    id: rowId
})

const setBillNumberInRowData = (rowData, billNumber) => ({
    ...rowData,
    billNumber: {
        ...rowData.billNumber,
        value: billNumber
    }
})

// Fills data into a partial row, useful when copying inputs from a preceding row
const updatePartialRow = (emptyPartialRow, rowWithDataToCopyOver) => (
    PARTIAL_FIELDS.reduce((agg, fieldId) => {
        return {
            ...agg,
            [fieldId]: {
                ...agg[fieldId],
                value: rowWithDataToCopyOver[fieldId].value,

                // annoying that this has to be included. code should be refactored.
                // when the partial row is created, displayError is false for all fields
                // but becomes true after unfocusing... however, partials have 3 disabled fields
                // and thus, those fields are impossible to unfocus
                displayError: true
            }
        }
    }, emptyPartialRow)
)

// Operation is a function that takes a bill number and applies some mathematical operation on it
// For example: (billNumber) => billNumber+1 or billNumber-1
const changeBillNumbers = (rowData, index, operation) => {
    if (index >= rowData.length) {
        return rowData;
    }
    else return rowData.map((item, idx) => {
        if (idx < index) {
            return item;
        }

        return {
            ...item,
            [RowFields.BillNumber.id]: {
                ...item[RowFields.BillNumber.id],
                value: operation(item[RowFields.BillNumber.id].value)
            }
        }
    })
}

// Returns the same row data with an updated rowType
const changeRowType = (rowData, index, newType) => {
    return rowData.map((rowObj, idx) => {
        if (idx !== index) {
            return rowObj;
        }

        return {
            ...rowObj,
            rowType: newType
        }
    })
}

const validateField = (field, value) => {
    switch (field) {
        case RowFields.AmountPaid.id:
            return value.match(/^(\d{1,3}(\d{3})*|(\d+))(\.\d{2})?$/) ? '' : 'Invalid format';
        default:
            // All fields besides Amount Paid are 'date' fields
            if (value.match(/\d{5,}/)) { // if the year has 5 or more digits
                return 'Invalid year';
            }
            const dateObj = new Date(value)
            if (isNaN(dateObj) || !value) {
                return 'Invalid date';
            }
            if (dateObj.getTime() - (new Date()).getTime() >= 0) {
                // date is in the future
                return "Date cannot be in the future!";
            }
            if (dateObj.getTime() - (new Date('01/01/1980')).getTime() <= 0) {
                return "Date cannot be before 1980, that's ridiculous."
            }
    }
}

// Assumes there can only be 1 normal row per group of billNumbers
const updateNormalContainsPartialsStatus = (rowData, billNumber, value) => {
    return rowData.map(row => {
        if ((row.rowType === "normal") && (row[RowFields.BillNumber.id].value === billNumber)) {
            return {
                ...row,
                "hasPartials": value
            }
        }
        else {
            return row;
        }
    })
}

const createInitialMetaData = () => {
    return Object.values(MetaFields).reduce((agg, field) => {
        return {
            ...agg,
            [field.id]: null
        }
    }, {})
}

const initialState = {
    rowData: [createRow(0, 1, "normal")],
    metaData: createInitialMetaData()
}

export default (state = initialState, action) => {
    switch (action.type) {
        case ON_ROW_INPUT_CHANGE: {
            const { index, field, value } = action;
            const errMsg = validateField(field.id, value);
            const displayError = state.rowData[index][field.id].displayError;
            const newRowData = updateFieldObjectInArray(state.rowData, index, field.id, { value, errMsg, displayError });
            return {
                ...state,
                rowData: newRowData
            }
        }
        case ON_META_FIELD_CHANGE: {
            return {
                ...state,
                metaData: {
                    ...state.metaData,
                    [action.field]: action.value
                }
            }
        }
        case ON_REMOVE_FOCUS: {
            const { index, field } = action;
            const { value } = state.rowData[index][field.id];
            const errMsg = validateField(field.id, value)
            const displayError = true;
            const newRowData = updateFieldObjectInArray(state.rowData, index, field.id, { value, errMsg, displayError });
            return {
                ...state,
                rowData: newRowData
            }
        }
        case INSERT_PARTIAL: {

            const parentRow = state.rowData[action.index];
            if (rowIsIncomplete(parentRow) || rowContainsError(parentRow)) {
                return {
                    ...state
                }
            }

            // Create partial row and copy data from preceding row
            const emptyPartialRow = createRow(action.rowId, action.billNumber, "partial");
            const filledPartialRow = updatePartialRow(emptyPartialRow, parentRow);
            const rowData = insertItem(state.rowData, action.index + 1, filledPartialRow);

            // Update the parent normal row's hasPartials status
            const newRowData = updateNormalContainsPartialsStatus(rowData, action.billNumber, true)
            return {
                ...state,
                rowData: newRowData
            }
        }
        case INSERT_ROW: {

            // Find the index to insert the new row (if trying to insert in a group of partials, put it at the bottom)
            const idxOfNextEndOfPartials = state.rowData.findIndex((_, idx) => {
                return (idx >= action.index) && (!doesRowHavePartials(state.rowData, idx));
            })
            const idxToInsertAt = idxOfNextEndOfPartials > -1 ? idxOfNextEndOfPartials : action.index;

            const rowDataWithIncrementedBillNumbers = changeBillNumbers(state.rowData, idxToInsertAt + 1, (billNumber) => billNumber + 1);
            const emptyRow = createRow(action.rowId, action.billNumber + 1, "normal");
            const newRowData = insertItem(rowDataWithIncrementedBillNumbers, idxToInsertAt + 1, emptyRow);
            return {
                ...state,
                rowData: newRowData
            }
        }
        case REMOVE_ROW: {
            if (state.rowData.length === 1) {
                return { ...state }
            }

            if (state.rowData[action.index].rowType === "partial") {
                // if a partial row is being removed, dont decrement rows

                let rowData, newRowData;

                // if this was the last partial of a group, the normal one needs to be updated
                //   to state that it doesnt contain partials anymore
                const numRowsWithThisBillNumber = state.rowData.filter(row => row[RowFields.BillNumber.id].value === action.billNumber).length
                if (numRowsWithThisBillNumber === 2) {
                    rowData = removeItem(state.rowData, action.index)
                    newRowData = updateNormalContainsPartialsStatus(rowData, action.billNumber, false)
                } else {
                    rowData = removeItem(state.rowData, action.index);
                    newRowData = updateNormalContainsPartialsStatus(rowData, action.billNumber, true);
                }

                return {
                    ...state,
                    rowData: newRowData
                }
            } else { // if it's a normal row...
                const currRowHasPartials = doesRowHavePartials(state.rowData, action.index);

                if (currRowHasPartials) {
                    // remove current one, don't decrement subsequent rows, change next partial to normal
                    const updatedNextRow = changeRowType(state.rowData, action.index + 1, "normal");
                    const rowData = removeItem(updatedNextRow, action.index);

                    // check if the new normal row has partial rows and update its status
                    const value = doesRowHavePartials(rowData, action.index);
                    const newRowData = updateNormalContainsPartialsStatus(rowData, action.billNumber, value);

                    return {
                        ...state,
                        rowData: newRowData
                    }
                } else {
                    const rowDataWithDecrementedBillNumbers = changeBillNumbers(state.rowData, action.index + 1, (billNumber) => billNumber - 1);
                    return {
                        ...state,
                        rowData: removeItem(rowDataWithDecrementedBillNumbers, action.index)
                    }
                }
            }
        }
        case CLEAR_ALL_FIELDS: {
            return initialState
        }
        default: {
            return initialState;
        }
    }
}